import dayjs from "dayjs";
import * as XLSX from "xlsx";

import { supabase } from "~/integrations/supabase/client";

export interface AddressExportData {
  address_id: string;
  client_name: string;
  client_tax_id: string;
  street: string;
  number: string;
  city: string;
  comuna: string;
  region: string;
}

/**
 * Fetches all addresses with client info for export
 */
export const fetchAllAddressesForExport = async (): Promise<
  AddressExportData[]
> => {
  // Use client_id hint to specify which FK relationship to use
  // (addresses.client_id -> clients.id, not clients.fiscal_address_id -> addresses.id)
  const { data, error } = await supabase
    .from("addresses")
    .select(
      `
      id,
      street,
      number,
      city,
      comuna,
      state,
      client_id,
      clients:client_id!inner (
        id,
        name,
        tax_id,
        deleted_at
      )
    `
    )
    .is("clients.deleted_at", null);

  if (error) throw error;

  if (!data) return [];

  return data.map((addr: any) => ({
    address_id: addr.id,
    client_name: addr.clients?.name || "",
    client_tax_id: addr.clients?.tax_id || "",
    street: addr.street || "",
    number: addr.number || "",
    city: addr.city || "",
    comuna: addr.comuna || "",
    region: addr.state || "",
  }));
};

/**
 * Exports addresses to Excel file
 */
export const exportAddressesToExcel = async (): Promise<void> => {
  const addresses = await fetchAllAddressesForExport();

  if (addresses.length === 0) {
    throw new Error("No se encontraron direcciones para exportar");
  }

  const excelData = addresses.map((addr) => ({
    "ID Dirección": addr.address_id,
    "Nombre Cliente": addr.client_name,
    "RUT Cliente": addr.client_tax_id,
    Calle: addr.street,
    Número: addr.number,
    Ciudad: addr.city,
    Comuna: addr.comuna,
    Región: addr.region,
  }));

  const workbook = XLSX.utils.book_new();

  // Create instructions sheet
  const instructions = [
    ["INSTRUCCIONES PARA ACTUALIZACIÓN DE DIRECCIONES"],
    [""],
    ["1. IDENTIFICACIÓN:"],
    [
      "   - Use la columna 'ID Dirección' para identificar cada dirección",
    ],
    ["   - NO cree nuevas filas - solo se permiten actualizaciones"],
    ["   - NO elimine filas existentes"],
    [""],
    ["2. COLUMNAS EDITABLES:"],
    ["   - Calle"],
    ["   - Número"],
    ["   - Ciudad"],
    ["   - Comuna"],
    ["   - Región"],
    [""],
    ["3. COLUMNAS NO EDITABLES (solo referencia):"],
    ["   - ID Dirección"],
    ["   - Nombre Cliente"],
    ["   - RUT Cliente"],
    [""],
    ["4. SUBIR ARCHIVO:"],
    ["   - Guarde como Excel (.xlsx)"],
    ["   - Use el botón 'Importar Direcciones' en la página de Clientes"],
  ];

  const instructionsSheet = XLSX.utils.aoa_to_sheet(instructions);
  instructionsSheet["!cols"] = [{ wch: 70 }];

  // Create data sheet
  const worksheet = XLSX.utils.json_to_sheet(excelData);
  worksheet["!cols"] = [
    { wch: 38 }, // ID Dirección
    { wch: 35 }, // Nombre Cliente
    { wch: 15 }, // RUT Cliente
    { wch: 40 }, // Calle
    { wch: 12 }, // Número
    { wch: 20 }, // Ciudad
    { wch: 20 }, // Comuna
    { wch: 30 }, // Región
  ];

  XLSX.utils.book_append_sheet(workbook, instructionsSheet, "Instrucciones");
  XLSX.utils.book_append_sheet(workbook, worksheet, "Direcciones");

  const currentDate = dayjs().format("YYYY-MM-DD");
  XLSX.writeFile(workbook, `direcciones_${currentDate}.xlsx`);
};
